import * as CL from '@design-system/component-library';
import { type JSX } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { SubscriptionCategory } from '../../common/enums.js';
import { addYourDeviceMsg, addYourSubscriptionMsg, pairingInfoMsg, t } from '../../common/i18n/index.js';
import { CompanySearch, PhoneNumber } from '../../common/react-hook-form/fields/index.js';
import { CATEGORY_URL } from '../../common/utils/categoryUtils.js';
import { SubscriptionPairingRequest } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider';
import type { CategoryKey } from '../../common/utils/categoryUtils.js';

type EoeSubscriptionPairingSceneProps = {
  category: string;
  mdmId: string;
};

const getPairingOptionsForCategory = (
  category: string
): {
  header: string;
  body: string;
  subscriptionType: SubscriptionPairingRequest.SubscriptionTypeEnum;
} => {
  switch (category) {
    case SubscriptionCategory.VOICE:
      return {
        body: t.UT8Z(pairingInfoMsg),
        header: t.P35B(addYourSubscriptionMsg),
        subscriptionType: SubscriptionPairingRequest.SubscriptionTypeEnum.MOBILE,
      };

    case SubscriptionCategory.BROADBAND:
      return {
        body: t.UT8Z(pairingInfoMsg),
        header: t.P35B(addYourSubscriptionMsg),
        subscriptionType: SubscriptionPairingRequest.SubscriptionTypeEnum.OTHER,
      };

    case SubscriptionCategory.DEVICE:
      return {
        body: t.UT8Z(pairingInfoMsg),
        header: t.SQW6(addYourDeviceMsg),
        subscriptionType: SubscriptionPairingRequest.SubscriptionTypeEnum.OTHER,
      };

    default:
      return {
        body: '',
        header: '',
        subscriptionType: SubscriptionPairingRequest.SubscriptionTypeEnum.OTHER,
      };
  }
};

interface PairingSceneFormSubmitProps {
  mdmId: string;
  phoneNumber: string;
  subscriptionType: SubscriptionPairingRequest.SubscriptionTypeEnum;
}

interface PairingSceneCompanyAndPhoneFormProps {
  mdmId?: string;
  subscriptionType: SubscriptionPairingRequest.SubscriptionTypeEnum;
  onSubmitForm: (data: PairingSceneFormSubmitProps) => void;
}

const PairingSceneCompanyAndPhoneForm = ({
  mdmId,
  subscriptionType,
  onSubmitForm,
}: PairingSceneCompanyAndPhoneFormProps) => {
  const { processSubscriptionPairing } = useAuth();
  const isMdmId = !(subscriptionType === SubscriptionPairingRequest.SubscriptionTypeEnum.OTHER && !mdmId);
  const methods = useForm({ mode: 'all', defaultValues: { masterId: '', phoneNumber: '' } });
  const onSubmit = ({ masterId, phoneNumber }: { masterId: string; phoneNumber: string }) => {
    const id = isMdmId ? mdmId : masterId;
    processSubscriptionPairing({ mdmId: id, msisdn: phoneNumber, subscriptionType });
    onSubmitForm({ mdmId: id || '', phoneNumber, subscriptionType });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
        {!isMdmId && <CompanySearch />}
        <PhoneNumber />
        <CL.Button block className={dsClass.MARGIN_TOP_6} size="m" type="submit">
          {t.F0MY('Next')}
        </CL.Button>
      </form>
    </FormProvider>
  );
};

const EoeSubscriptionPairingSceneContent = ({ category, mdmId }: EoeSubscriptionPairingSceneProps): JSX.Element => {
  const navigate = useNavigate();
  const pairingOptions = getPairingOptionsForCategory(category);

  const onSubmit = (data: PairingSceneFormSubmitProps) => {
    navigate(
      generatePath(paths.EMPLOYEE_SUBSCRIPTIONS_VERIFY_ADD, { categoryUrl: CATEGORY_URL[category as CategoryKey] }),
      {
        state: {
          params: { mdmId: data.mdmId, mobileNumber: data.phoneNumber, subscriptionType: data.subscriptionType },
          prevPath: location.pathname,
        },
      }
    );
  };

  return (
    <PairingSceneCompanyAndPhoneForm
      mdmId={mdmId}
      subscriptionType={pairingOptions.subscriptionType}
      onSubmitForm={onSubmit}
    />
  );
};

export const EoeSubscriptionPairingScene = ({ category, mdmId }: EoeSubscriptionPairingSceneProps) => {
  const pairingOptions = getPairingOptionsForCategory(category);

  return (
    <div className="of-employee__form-container">
      <div className={`${dsClass.MARGIN_HORIZONTAL_3} ${dsClass.MARGIN_TOP_3}`}>
        <h3>{pairingOptions.header}</h3>
        <p>{pairingOptions.body}</p>
        <EoeSubscriptionPairingSceneContent mdmId={mdmId} category={category} />
      </div>
    </div>
  );
};
